import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle, WifiOff, Info } from 'lucide-react';
import { MaintenanceBlockScreen } from '../components/MaintenanceBanner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, resumeOffline } = useAuth();
  const { isOnline, enterOfflineMode } = useOffline();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const [offlineUser, setOfflineUser] = useState(null);

  // Check for redirect param (e.g., from session expiry on count pages)
  const redirectTo = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('redirect') || '/home';
    } catch { return '/home'; }
  }, []);

  // Show offline indicator and check for resumable user
  useEffect(() => {
    if (!isOnline) {
      setInfo('You are offline. Previously logged-in users can still sign in.');
      // Check localStorage first
      try {
        const raw = localStorage.getItem('offline_last_user');
        if (raw) {
          const { user } = JSON.parse(raw);
          if (user) { setOfflineUser(user); return; }
        }
      } catch { /* ignore */ }
      // Fallback: check IndexedDB for stocktake packages
      (async () => {
        try {
          const db = await new Promise((resolve, reject) => {
            const req = indexedDB.open('abbeyzone_offline');
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => reject(req.error);
          });
          const storeNames = Array.from(db.objectStoreNames);
          if (storeNames.includes('stocktake_packages')) {
            const pkgs = await new Promise((resolve) => {
              const tx = db.transaction('stocktake_packages', 'readonly');
              const store = tx.objectStore('stocktake_packages');
              const ga = store.getAll();
              ga.onsuccess = () => resolve(ga.result || []);
              ga.onerror = () => resolve([]);
            });
            if (pkgs.length > 0) {
              setOfflineUser({ first_name: 'Offline', last_name: 'User', email: 'offline@device' });
            }
          }
          db.close();
        } catch { /* ignore */ }
      })();
    } else {
      setInfo('');
      setOfflineUser(null);
    }
  }, [isOnline]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double-submit
    if (loading) return;
    
    setError('');
    setLoading(true);

    try {
      // iter360 — trim whitespace + Outlook NBSP/ZWSP to defend against
      // temp passwords being mangled when copy-pasted from email.
      const TRIM_CHARS = /^[\s\u00A0\u2007\u202F\u200B\u200C\u200D\uFEFF]+|[\s\u00A0\u2007\u202F\u200B\u200C\u200D\uFEFF]+$/g;
      const cleanEmail = (email || '').replace(TRIM_CHARS, '');
      const cleanPassword = (password || '').replace(TRIM_CHARS, '');
      const result = await login(cleanEmail, cleanPassword);
      
      // If offline login, enter offline mode
      if (result.isOffline) {
        enterOfflineMode();
      }
      
      // Navigate on success
      navigate(redirectTo);
    } catch (err) {
      // Stay on same page, show error
      let errorMessage = err.response?.data?.detail || err.message || 'Invalid email or password.';
      
      // Enhance message for offline-specific errors
      if (errorMessage === 'First login requires internet connection.') {
        errorMessage = 'First login requires internet connection. Please connect to the internet to sign in for the first time on this device.';
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Resume offline session without password
  const handleResumeOffline = async () => {
    const result = await resumeOffline();
    if (result.success) {
      enterOfflineMode();
      navigate(redirectTo);
    } else {
      setError('Could not resume offline session. Please connect to the internet to sign in.');
    }
  };

  // Clear error when user edits email or password
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (error) setError('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (error) setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="login-page">
      <MaintenanceBlockScreen />
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {/* Logo enlarged 30% from original (80px to 104px) */}
            <img src="/logo.png" alt="Abbey 5 Star Ltd" className="w-auto object-contain" style={{ height: '104px' }} />
          </div>
          <CardTitle className="text-2xl font-bold">Abbey 5 Star Ltd</CardTitle>
          <CardDescription>Access your schedules, reports, and operational insights</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Offline resume button — shown when offline and a previous user exists */}
          {!isOnline && offlineUser && (
            <div className="mb-4">
              <Button
                type="button"
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleResumeOffline}
                data-testid="offline-resume-btn"
              >
                <WifiOff className="mr-2 h-4 w-4" />
                Continue as {offlineUser.first_name || offlineUser.email} (Offline)
              </Button>
              <p className="text-xs text-center text-muted-foreground mt-1.5">Resume your previous session without internet</p>
              <div className="relative my-3">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t" /></div>
                <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-muted-foreground">or sign in with credentials</span></div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Offline info banner */}
            {info && (
              <Alert className="bg-amber-50 border-amber-200" data-testid="offline-info">
                <WifiOff className="h-4 w-4 text-amber-600" />
                <AlertDescription className="ml-2 text-amber-800">{info}</AlertDescription>
              </Alert>
            )}
            
            {error && (
              <Alert variant="destructive" data-testid="login-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="ml-2">{error}</AlertDescription>
              </Alert>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                required
                disabled={loading}
                data-testid="login-email-input"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link 
                  to="/forgot-password" 
                  className="text-sm text-primary hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={handlePasswordChange}
                required
                disabled={loading}
                data-testid="login-password-input"
              />
            </div>
            
            <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Signing in...</>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>
          
          {/* Offline login hint */}
          {!isOnline && (
            <div className="mt-4 p-3 bg-slate-100 rounded-md">
              <div className="flex items-start gap-2 text-xs text-muted-foreground">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Offline Login</p>
                  <p>If you've logged in before on this device, you can still access your cached schedule data.</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
